import { useEffect, useRef, useState, type ReactNode } from 'react'
import { FONDOS_LOGIN, fondoInicial } from '@/lib/login-fondos'

/**
 * El marco de la pantalla de entrada: un modal centrado con la foto a un lado
 * y el formulario al otro, sobre esa misma foto difuminada a pantalla completa.
 *
 * Es **uno solo para los tres accesos** —ERP, Clientes y Partners—. Antes el
 * mismo `/login` dibujaba dos cosas distintas según el hostname: el portal se
 * llevaba un panel con foto y el ERP una tarjeta centrada y pelada. Dos
 * diseños para el mismo formulario es el doble de trabajo y la mitad del
 * cuidado en cada uno.
 *
 * Tres decisiones que vale la pena no re-discutir:
 *
 * 1. **El fondo es la misma foto, no una segunda imagen.** Se baja una vez y
 *    el navegador la sirve de su caché para el panel; el difuminado lo pone
 *    CSS. Un fondo aparte sería el doble de bytes para algo que ni se mira.
 *
 * 2. **El primer pintado ya tiene fondo**, porque sale del LQIP incrustado en
 *    el bundle (32 px en base64). La foto de verdad entra encima con una
 *    transición cuando termina de bajar. Sin eso el login abre con un
 *    rectángulo gris, que es justo lo contrario de premium.
 *
 * 3. **Sólo se descarga la foto actual y la siguiente.** Cargar las cinco de
 *    entrada son ~430 KB para ver una.
 *
 * El login va siempre en claro, sin seguir el tema del ERP: está sobre una
 * fotografía y no hay todavía ninguna pantalla de trabajo alrededor. El tema
 * es una preferencia del espacio de trabajo; esto es la puerta.
 */

const INTERVALO_MS = 9000

interface Props {
  /** Lo que va en el panel derecho: cabecera y formulario. */
  children: ReactNode
  /** Pie del panel (aviso de copyright). Va abajo a la derecha. */
  pie?: ReactNode
  /**
   * Salida alternativa, abajo a la izquierda (hoy: entrar con QR). Va en el
   * pie y no dentro del formulario para que el centro quede con una sola
   * cosa que hacer — y de paso ocupa el espacio que sobraba al fondo del
   * panel, que estaba vacio.
   */
  accion?: ReactNode
}

export default function LoginShell({ children, pie, accion }: Props) {
  const [indice, setIndice] = useState(fondoInicial)
  // Qué fotos pueden pedir bytes. Empieza con la que se ve; la siguiente entra
  // en cuanto la actual terminó de bajar, para que el cambio sea instantáneo.
  const [habilitadas, setHabilitadas] = useState<Set<number>>(() => new Set([indice]))
  const [cargadas, setCargadas] = useState<Set<number>>(() => new Set())
  const detenido = useRef(false)

  const siguiente = (i: number) => (i + 1) % FONDOS_LOGIN.length

  // Quien pidió menos movimiento no quiere un carrusel: se queda con una foto.
  useEffect(() => {
    detenido.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }, [])

  useEffect(() => {
    if (detenido.current) return
    const t = setInterval(() => setIndice((i) => siguiente(i)), INTERVALO_MS)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Precarga de la siguiente, una sola vez por foto.
  useEffect(() => {
    if (detenido.current) return
    const prox = siguiente(indice)
    if (habilitadas.has(prox)) return
    const img = new Image()
    img.src = FONDOS_LOGIN[prox].url
    img.onload = () => {
      setHabilitadas((s) => new Set(s).add(prox))
      setCargadas((s) => new Set(s).add(prox))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indice])

  const fondo = FONDOS_LOGIN[indice]

  return (
    <div className="login-shell">
      {/* Fondo: el LQIP siempre puesto, y encima la foto cuando está. */}
      <div className="login-shell__bg" style={{ backgroundImage: `url(${fondo.lqip})` }} aria-hidden="true" />
      {FONDOS_LOGIN.map((f, i) => habilitadas.has(i) && (
        <div
          key={f.url}
          className={`login-shell__bg login-shell__bg--hd${i === indice && cargadas.has(i) ? ' is-active' : ''}`}
          style={{ backgroundImage: `url(${f.url})` }}
          aria-hidden="true"
        />
      ))}
      <div className="login-shell__wash" aria-hidden="true" />

      <div className="login-modal" role="dialog" aria-labelledby="login-titulo">
        {/* La miniatura también va de fondo del panel, no sólo de la pantalla:
            sin esto, mientras la foto baja el panel queda liso y el modal se
            ve partido en dos mitades vacías. Es el mismo recurso de siempre —
            los colores de la foto puestos antes que la foto. */}
        <aside className="login-art" style={{ backgroundImage: `url(${fondo.lqip})` }}>
          {FONDOS_LOGIN.map((f, i) => habilitadas.has(i) && (
            <img
              key={f.url}
              src={f.url}
              alt={i === indice ? f.alt : ''}
              className={`login-art__foto${i === indice && cargadas.has(i) ? ' is-active' : ''}`}
              style={{ objectPosition: f.foco }}
              // La primera no es `lazy`: es lo que se está mirando.
              loading="eager"
              decoding="async"
              onLoad={() => setCargadas((s) => new Set(s).add(i))}
            />
          ))}
          <div className="login-art__veil" aria-hidden="true" />

          <img
            src="/assets/brand/logo-full-white.svg"
            alt="1Conecta"
            className="login-art__logo"
            height={26}
          />

          {/* `key` fuerza el remonte en cada cambio: es lo que vuelve a
              disparar la animación de entrada de la frase. */}
          <p className="login-art__frase" key={fondo.url}>{fondo.frase}</p>

          {/* Sin indicadores de posición a propósito: esto no es un carrusel
              que haya que recorrer, es un fondo que cambia solo. Unos puntitos
              invitan a pulsarlos y convierten en tarea lo que debía ser
              ambiente, en la pantalla donde la única tarea es entrar. */}
        </aside>

        <div className="login-panel">
          <div className="login-panel__inner">
            {/* En pantalla angosta el panel de la foto no entra, así que el
                logo se repite acá: sin esto la pantalla de entrada del móvil
                no dice de quién es. */}
            <img
              src="/assets/brand/logo-full-color.svg"
              alt="1Conecta"
              className="login-panel__logo"
              height={24}
            />
            {children}
          </div>
          {(pie || accion) && (
            <div className="login-panel__pie">
              {accion}
              <span className="login-panel__legal">{pie}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
